import L, { LatLngExpression } from 'leaflet';
import { MarkerClusterGroup } from 'leaflet.markercluster/src';

type TMarker = {
    coordinates: LatLngExpression;
    iconUrl: string;
    text: string;
};

type TMarkers = { [key: string]: TMarker };

const mapJsClass = 'js-map';

const mapElements = document.querySelectorAll('.' + mapJsClass) as NodeListOf<HTMLElement>;

const defaultZoom = 15;
const defaultCoordinates: LatLngExpression = [48.8546434, 2.3486765];

mapElements.forEach((mapElement) => {
    // Create map
    const map = L.map(mapElement, {
        zoomControl: false
    });

    // Give map a tileLayer
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 20
    }).addTo(map);

    // Get geojson from data attribute
    const geojson = mapElement.dataset.geojson && JSON.parse(mapElement.dataset.geojson);

    if (geojson) {
        L.geoJSON(geojson, {
            onEachFeature: (feature, layer) => {
                // Add popup to feature
                if (feature.properties && feature.properties.popupContent) {
                    layer.bindPopup(feature.properties.popupContent);
                }
            },
            style: (feature) => {
                // Set feature color
                if (feature.properties && feature.properties.color) {
                    return { color: feature.properties.color };
                }
            }
        }).addTo(map);
    }

    // Get markers from data attribute
    const markers: TMarkers = mapElement.dataset.markers && JSON.parse(mapElement.dataset.markers);

    const markersCoordinates = [];

    // Create cluster group
    const markerClusterGroup = new MarkerClusterGroup({
        showCoverageOnHover: false
    });

    if (markers) {
        // For each marker
        Object.keys(markers).forEach((key) => {
            // Add coordinates to array
            markersCoordinates.push(markers[key].coordinates);

            // Create custom marker icon
            const markerIcon = L.icon({
                iconUrl: markers[key].iconUrl,
                iconSize: [32, 32],
                iconAnchor: [16, 32]
            });

            // Create marker
            const marker = L.marker(markers[key].coordinates, { icon: markerIcon });

            if (markers[key].text) {
                // Bind popup to marker
                marker.bindPopup(getPopupContent(markers[key])).openPopup();
            }

            // Add marker to cluster group
            markerClusterGroup.addLayer(marker);
        });

        // Add cluster group to map
        map.addLayer(markerClusterGroup);
    }

    // Get zoom from data attribute
    const zoom = mapElement.dataset.zoom ? JSON.parse(mapElement.dataset.zoom) : defaultZoom;

    // Get zoom control labels from the data attribute
    const zoomInTitle = mapElement.dataset.zoomInTitle;
    const zoomOutTitle = mapElement.dataset.zoomOutTitle;

    // Create zoom control labels
    if (zoomInTitle && zoomOutTitle) {
        L.control.zoom({
            zoomInTitle: zoomInTitle,
            zoomOutTitle: zoomOutTitle
        }).addTo(map);
    }

    // Get centering coordinates from data attribute
    const centeringCoordinates: LatLngExpression = mapElement.dataset.centeringCoordinates && JSON.parse(mapElement.dataset.centeringCoordinates);

    // Center map
    if (centeringCoordinates) {
        // Manual centering
        map.setView(centeringCoordinates, zoom);
    } else if (markersCoordinates.length > 1) {
        // Multiple markers
        const mapBounds = new L.LatLngBounds(markersCoordinates);
        map.fitBounds(mapBounds, { maxZoom: 18 });
    } else if (markersCoordinates.length === 1) {
        // One marker
        map.setView(markersCoordinates[0], zoom);
    } else {
        // No markers
        map.setView(defaultCoordinates, zoom);
    }
});

function getPopupContent (marker: TMarker): string {
    const { text } = marker;

    const componentClass = 'c-map-popup';

    return `
        <div class="${componentClass}">
            <div class="${componentClass}__text">${text}</div>
        </div>
    `;
}